import httpClient from '../../httpClient';
import { ListingTier } from '../../../models/Listing';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import { InvitedBroker } from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/models/InvitedBroker';
import { AxiosRequestConfig } from 'axios';
import { User } from 'models/User';
import {
  BossModeStatsFilters,
  BossModeTrack,
} from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/pages/BossModeStats/types';
import {
  DashboardMetric,
  PaymentInvoiceSkippedFormProps,
} from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/pages/ListingMetrics/types';
import { getFileParams, getUploadedFile } from 'uploadFileUtils';
import { TransferResponse } from 'models/DealRoom';
import { AddBossModeAccountForm } from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/pages/BossModeAccounts/components/AddBossMode/components/AddBossModeAccount/AddBossModeAccount.component';
import { FilePreview, UploadedFileResponse } from 'models/File';
import { ActivityMetricsTableData } from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/pages/ActivityMetrics/components/ActivityMetricsTable/data';
import { SuggestedActionsTableData } from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/pages/BossModeStats/pages/SuggestedActions/components/SuggestedActionsTable/data';
import { PendingActionsDetailResponse } from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/pages/BossModeStats/pages/SuggestedActions/components/SuggestedActionsDrawer/types';
import { Params } from 'react-router-dom';

function changeTier(listingId: string, listingTier: ListingTier) {
  return httpClient.post(`portfolio/admin/listings/${listingId}/change-tier`, {
    listingTier,
  });
}

function addABroker(values: InvitedBroker) {
  return httpClient.post('admin/invite-broker', values);
}

function getInvitedBrokers(params?: PaginationParams) {
  return httpClient.get<PaginatedData<InvitedBroker>>('admin/brokers', {
    params,
  });
}

async function uploadBrokerFirmLogo(
  file: File,
  fileName: string,
  config?: AxiosRequestConfig,
) {
  const fileParams = getFileParams(fileName, file);

  const { data } = await httpClient.post<UploadedFileResponse>(
    `admin/upload-broker-firm-logo-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

type CheckTransferData = {
  isBrokerExist: boolean;
  canBeListingOwner: boolean;
};

function checkTransferEmail(email: string, dealRoomId: string) {
  return httpClient.post<CheckTransferData>(
    'portfolio/admin/check-transfer-email',
    {
      email,
      dealRoomId,
    },
  );
}

function transferToColleague(
  listingId: string,
  email: string,
  dealRoomId: string,
) {
  return httpClient.post<TransferResponse>(
    `portfolio/admin/listings/${listingId}/transfer-listing`,
    {
      email,
      dealRoomId,
    },
  );
}

function transferToNewUser(
  listingId: string,
  user: Partial<User>,
  dealRoomId: string,
) {
  return httpClient.post<TransferResponse>(
    `portfolio/admin/listings/${listingId}/transfer-listing`,
    {
      ...user,
      dealRoomId,
    },
  );
}

function deleteBrokerFirmLogo(fileKey: string) {
  return httpClient.delete(`admin/delete-broker-firm-logo?fileKey=${fileKey}`);
}

function remindUserVerify(listingId: string, email: string) {
  return httpClient.post(
    `/portfolio/admin/listings/${listingId}/transfer-listing-reminder`,
    { email },
  );
}

function getDashboardMetrics(params: PaginationParams & Params) {
  return httpClient.get<PaginatedData<DashboardMetric>>(
    '/admin/dashboard-metrics',
    {
      params,
    },
  );
}

function getBrokerSearchvalues(searchValue: string) {
  return httpClient.get<{ brokers: string[] }>(
    `/admin/dashboard-metrics/brokers/${searchValue}`,
  );
}

function getListingSearchvalues(searchValue: string) {
  return httpClient.get<{ listings: string[] }>(
    `/admin/dashboard-metrics/listings/${searchValue}`,
  );
}

function getBossModeTracks(params?: PaginationParams & BossModeStatsFilters) {
  return httpClient.get<PaginatedData<BossModeTrack>>(
    'admin/boss-mode-tracks',
    { params },
  );
}

function getAllUsers(params?: PaginationParams) {
  return httpClient.get<PaginatedData<User>>('admin/search-users', { params });
}

function getBrokers(params?: PaginationParams) {
  return httpClient.get<Array<{ name: string; userId: string }>>(
    'admin/brokers-with-deal',
    {
      params,
    },
  );
}

function getAllBMUsers(params?: PaginationParams) {
  return httpClient.get<Array<{ name: string; userId: string }>>(
    'admin/users',
    {
      params,
    },
  );
}

function getLeads(params?: PaginationParams) {
  return httpClient.get<Array<{ name: string; buyerId: string }>>(
    'admin/leads',
    {
      params,
    },
  );
}

function getDealRooms(params?: PaginationParams) {
  return httpClient.get<Array<{ name: string; dealRoomId: string }>>(
    'admin/deal-rooms',
    {
      params,
    },
  );
}

function getDashboardMetricsCsv() {
  return httpClient.get<FilePreview>('/admin/dashboard-metrics/export-csv');
}

function getDealsCsv() {
  return httpClient.get<FilePreview>('/admin/deals/export-csv');
}

function paymentInvoiceRespond(
  id: string,
  data: PaymentInvoiceSkippedFormProps,
) {
  return httpClient.post<DashboardMetric>(
    `/admin/boss-mode-tracks/${id}/payment-respond`,
    data,
  );
}

function getBossModeAccounts(params?: PaginationParams) {
  return httpClient.get<PaginatedData<User>>('/boss-mode-users', {
    params,
  });
}

function removeBossModeAccount(
  bossModeId: string,
  removeAccountFromPlatform: boolean,
) {
  return httpClient.post(`/boss-mode-users/${bossModeId}/strip`, {
    removeAccountFromPlatform,
  });
}

function addBossModeAccount(values: Partial<AddBossModeAccountForm>) {
  return httpClient.post('/boss-mode-users/invite', values);
}

function promoteBossModeAccount(userIds: string[]) {
  return httpClient.post('/boss-mode-users/promote', { userIds });
}

function searchUsersForBossModeAccount(params: PaginationParams) {
  return httpClient.get<PaginatedData<User>>('/boss-mode-users/search', {
    params,
  });
}

function getActivityMetrics(params: PaginationParams) {
  return httpClient.get<PaginatedData<ActivityMetricsTableData>>(
    'admin/activity-metrics',
    {
      params,
    },
  );
}

function getBossModeSuggestedActions(
  params?: PaginationParams & {
    userId?: string;
    dealRoomIds?: string;
    timeframe?: string;
  },
) {
  return httpClient.get<PaginatedData<SuggestedActionsTableData>>(
    'admin/boss-mode-operations/pending-actions',
    { params },
  );
}

function getBossModeDealRoomSuggestedActions(dealRoomId: string) {
  return httpClient.get<PendingActionsDetailResponse>(
    `admin/boss-mode-operations/deal-rooms/${dealRoomId}/pending-actions`,
  );
}

export default {
  changeTier,
  checkTransferEmail,
  transferToColleague,
  transferToNewUser,
  addABroker,
  getInvitedBrokers,
  uploadBrokerFirmLogo,
  deleteBrokerFirmLogo,
  remindUserVerify,
  getDashboardMetrics,
  getBrokerSearchvalues,
  getListingSearchvalues,
  getBossModeTracks,
  getAllUsers,
  getBrokers,
  getLeads,
  getDealRooms,
  getAllBMUsers,
  getDashboardMetricsCsv,
  getDealsCsv,
  paymentInvoiceRespond,
  getBossModeAccounts,
  removeBossModeAccount,
  addBossModeAccount,
  searchUsersForBossModeAccount,
  promoteBossModeAccount,
  getActivityMetrics,
  getBossModeSuggestedActions,
  getBossModeDealRoomSuggestedActions,
};
